import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '../../../../components/ui/Dialog';
import { archiveTournament } from '@/api/TournamentManagement/tournamentApi';
import type { TournamentView } from '@/models/tournament';
import { TournamentStatus, resolveEnum } from '@/models/tournament';

interface DeleteTournamentModalProps {
  isOpen: boolean;
  onClose: () => void;
  tournament: TournamentView | null;
  onDeleted?: () => void;
}

const DeleteTournamentModal: React.FC<DeleteTournamentModalProps> = ({ isOpen, onClose, tournament, onDeleted }) => {
  const { t } = useTranslation('tournament');
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [apiError, setApiError] = useState<string | null>(null);

  const statusKeys: Record<number, string> = {
    0: 'Upcoming',
    1: 'Active',
    2: 'Completed',
    3: 'Cancelled',
  };

  const handleDelete = async () => {
    if (!tournament) return;

    setIsDeleting(true);
    setApiError(null);

    try {
      const result = await archiveTournament(tournament.id);

      if (result.errors.length > 0) {
        setApiError(result.errors[0]?.description || t('Failed to delete tournament'));
        return;
      }

      setConfirmText('');
      onDeleted?.();
      onClose();
    } catch (error: any) {
      const msg = error?.response?.data?.errors?.[0]?.description
        || error?.message
        || t('Something went wrong. Please try again.');
      setApiError(msg);
      console.error('Error deleting tournament:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const isConfirmValid = confirmText.toLowerCase() === 'delete';

  // Radix Dialog, not a hand-rolled `fixed inset-0` div — see the note in AddTournamentModal.
  // A confirmation for an irreversible action is precisely the case where a missing focus trap
  // matters most: keyboard focus used to stay behind the overlay, on the page's own controls.
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => { if (!open && !isDeleting) onClose(); }}
    >
      <DialogContent
        size="sm"
        onEscapeKeyDown={(e) => { if (isDeleting) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (isDeleting) e.preventDefault(); }}
        onInteractOutside={(e) => { if (isDeleting) e.preventDefault(); }}
        data-testid="delete-tournament-modal"
      >
        <DialogHeader className="bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 rounded-t-xl">
          <DialogTitle className="flex items-center text-red-900 dark:text-red-200">
            <Icon name="AlertTriangle" size={24} className="me-2 text-red-600 dark:text-red-400" />
            {t('Delete Tournament')}
          </DialogTitle>
        </DialogHeader>

        {/* DialogBody, not a bare child of DialogContent: direct children get zero padding. */}
        <DialogBody className="space-y-4">
          {/* API Error */}
          {apiError && (
            <div role="alert" className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-start">
                <Icon name="AlertCircle" size={18} className="text-red-600 dark:text-red-400 me-2 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-800 dark:text-red-300">{apiError}</p>
              </div>
            </div>
          )}

          {/* Warning Message */}
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-start">
              <Icon name="AlertCircle" size={20} className="text-red-600 dark:text-red-400 me-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-red-900 dark:text-red-200 mb-1">{t('Warning: This action cannot be undone', { nsSeparator: false })}</h3>
                <p className="text-sm text-red-700 dark:text-red-300">
                  {t('Deleting this tournament will permanently remove all associated data, including matches, referee assignments, and historical records.')}
                </p>
              </div>
            </div>
          </div>

          {/* Tournament Details */}
          <div className="bg-muted border border-border rounded-lg p-4">
            <h4 className="text-sm font-semibold text-foreground mb-3">{t('Tournament to be deleted:', { nsSeparator: false })}</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-muted-foreground">{t('Name:', { nsSeparator: false })}</span>
                <span className="text-sm font-medium text-foreground">{tournament?.name}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-muted-foreground">{t('ID:', { nsSeparator: false })}</span>
                <span className="text-sm font-medium text-foreground">{tournament?.id}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-muted-foreground">{t('Matches:', { nsSeparator: false })}</span>
                <span className="text-sm font-medium text-foreground">{tournament?.matchCount}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-muted-foreground">{t('Status:', { nsSeparator: false })}</span>
                <span className="text-sm font-medium text-foreground">
                  {tournament ? t(statusKeys[(resolveEnum(TournamentStatus, tournament.status) as TournamentStatus)] ?? 'Unknown') : ''}
                </span>
              </div>
            </div>
          </div>

          {/* Confirmation Input — ui/Input so the label is a real htmlFor/id pair. The old sibling
              <label> named nothing, leaving the field that gates an irreversible action unnamed. */}
          <Input
            type="text"
            label={t('Type DELETE to confirm:', { nsSeparator: false })}
            value={confirmText}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmText(e.target.value)}
            placeholder={t('Type DELETE')}
            disabled={isDeleting}
            autoComplete="off"
            data-testid="delete-tournament-confirm-input"
          />
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isDeleting}
            data-testid="delete-tournament-cancel-btn"
          >
            {t('Cancel')}
          </Button>
          {/* variant="destructive", not hand-rolled `bg-red-600 text-white` classes: the shared
              destructive variant is what the rest of the app uses for irreversible submits, and
              class overrides also lost the variant's disabled/hover states. */}
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={!isConfirmValid || isDeleting}
            loading={isDeleting}
            data-testid="tournament-delete-btn"
          >
            {t('Delete Tournament')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteTournamentModal;
